/* eslint-disable max-lines */
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import moment from 'moment'

import { LocalFile } from '@types'

import { mapFileToLocalFile } from '../mappers'

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297

const MM_TO_PT = 2.83465
const A4_WIDTH_PT = A4_WIDTH_MM * MM_TO_PT
const A4_HEIGHT_PT = A4_HEIGHT_MM * MM_TO_PT

export enum ExportType {
  PDF = 'pdf',
  PNG = 'png',
  JPG = 'jpg',
  EXCEL = 'excel',
}

interface ExportOptions {
  fileName: string
  blob?: Blob
  onProgress?: (progress: number) => void
  onStart?: () => void
  onComplete?: () => void
}

interface UseExportReturnType {
  exportToPDF: (
    ref: HTMLElement,
    options: ExportOptions & {
      planningType: string
      orientation: 'portrait' | 'landscape'
    },
  ) => Promise<void>
  exportAsImage: (ref: HTMLElement, options: ExportOptions) => Promise<LocalFile>
}

const yieldToMainThread = () => new Promise((resolve) => setTimeout(resolve, 0))

const heavyTaskDelay = (ms: number = 16) => new Promise((resolve) => setTimeout(resolve, ms))

export const useExport = (): UseExportReturnType => {
  const exportToPDF = async (
    ref: HTMLElement,
    options: ExportOptions & {
      planningType: string
      orientation: 'portrait' | 'landscape'
    },
  ): Promise<void> => {
    try {
      options.onStart?.()
      options.onProgress?.(0)

      options.onProgress?.(10)
      const canvas = await captureElementAsCanvas(ref)

      await yieldToMainThread()
      options.onProgress?.(30)

      const imgData = canvas.toDataURL('image/png', 1.0)
      options.onProgress?.(50)

      await heavyTaskDelay(20)

      const pdf = await generatePDFInChunks(imgData, options)

      options.onProgress?.(90)

      await yieldToMainThread()

      const fileName = createFileName(options.fileName, ExportType.PDF)
      pdf.save(fileName)

      options.onProgress?.(100)
      options.onComplete?.()
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      throw error
    }
  }

  const generatePDFInChunks = async (
    imgData: string,
    options: ExportOptions & { planningType: string; orientation: 'portrait' | 'landscape' },
  ): Promise<jsPDF> => {
    const orientation = options.orientation
    const isLandscape = orientation === 'landscape'

    const pageWidth = isLandscape ? A4_HEIGHT_PT : A4_WIDTH_PT
    const pageHeight = isLandscape ? A4_WIDTH_PT : A4_HEIGHT_PT

    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'pt',
      format: 'a4',
      compress: true,
    })

    await yieldToMainThread()

    const documentMargin = 35
    const headerImageGap = 20
    const headerPadding = 12

    pdf.setFontSize(14)
    const textHeight = 7
    const headerHeight = textHeight + 2 * headerPadding

    pdf.setFillColor(245, 245, 245)
    pdf.rect(documentMargin, documentMargin, pageWidth - 2 * documentMargin, headerHeight, 'F')

    await yieldToMainThread()

    pdf.setTextColor(20, 23, 31)

    const textX = documentMargin + headerPadding
    const textY = documentMargin + headerPadding + textHeight * 1.2

    pdf.text(`Planning travaux - ${options.planningType}`, textX, textY)

    const topPosition = documentMargin + headerHeight + headerImageGap
    const availableWidth = pageWidth - 2 * documentMargin
    const availableHeight = pageHeight - topPosition - documentMargin

    const img = new Image()

    return new Promise((resolve, reject) => {
      img.onload = async () => {
        try {
          const imageRatio = img.width / img.height
          const pageRatio = availableWidth / availableHeight

          let finalWidth: number
          let finalHeight: number

          if (imageRatio > pageRatio) {
            finalWidth = availableWidth
            finalHeight = finalWidth / imageRatio
          } else {
            finalHeight = availableHeight
            finalWidth = finalHeight * imageRatio
          }

          const xPosition = documentMargin + (availableWidth - finalWidth) / 2

          await heavyTaskDelay(10)

          pdf.addImage(imgData, 'PNG', xPosition, topPosition, finalWidth, finalHeight)

          await yieldToMainThread()

          const borderWidth = 0.3
          pdf.setDrawColor(223, 226, 234)
          pdf.setLineWidth(borderWidth)
          pdf.rect(xPosition, topPosition, finalWidth, finalHeight, 'S')

          resolve(pdf)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = reject
      img.src = imgData
    })
  }

  const exportAsImage = async (ref: HTMLElement, options: ExportOptions): Promise<LocalFile> => {
    try {
      options.onStart?.()

      const canvas = await captureElementAsCanvas(ref)

      await yieldToMainThread()

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to convert canvas to blob'))
              return
            }

            const file = new File([blob], `${options.fileName}.png`, {
              type: 'image/png',
              lastModified: Date.now(),
            })

            const localFile = mapFileToLocalFile(file)

            options.onComplete?.()

            resolve(localFile)
          },
          'image/png',
          1.0,
        )
      })
    } catch (error) {
      console.error('Error exporting element as image:', error)
      throw error
    }
  }

  const createFileName = (fileName: string, type: ExportType): string => {
    const timestamp = moment().format('YYYY-MM-DD_HH-mm')
    return `${fileName.replace(' ', '-')}_${timestamp}.${type}`
  }

  const captureElementAsCanvas = async (ref: HTMLElement): Promise<HTMLCanvasElement> => {
    return html2canvas(ref, {
      scrollY: -window.scrollY,
      height: ref.scrollHeight,
      windowHeight: ref.scrollHeight,
      allowTaint: true,
      useCORS: true,
      backgroundColor: null,
      logging: false,
      scale: 3,
      ignoreElements: (element) => {
        return element.classList.contains('hover')
      },
    })
  }

  return {
    exportToPDF,
    exportAsImage,
  }
}
